import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  getMealEntitlements, assignMealEntitlement, deleteMealEntitlement,
} from '../../services/mealEntitlementService';
import { getMeals } from '../../services/mealService';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { EMPTY_TARGETS } from '../../components/ui/AssignmentTargetPicker';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import PersonCell from '../../components/ui/PersonCell';

const Container = styled.div``;

const INITIAL_FORM = Object.freeze({
  mealId: null,
  targets: { ...EMPTY_TARGETS },
  validFrom: null,
  validUntil: null,
  dailyLimitOverride: '',
  monthlyLimitOverride: '',
});

export default function MealEntitlementsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [meals, setMeals] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [initialValues, setInitialValues] = useState(INITIAL_FORM);

  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setFetching(true);
    getMealEntitlements({ pageSize: 200 })
      .then((res) => {
        const result = res.data?.data;
        setData(Array.isArray(result) ? result : (result?.data || []));
      })
      .finally(() => setFetching(false));
  }, [refreshTick]);

  useEffect(() => {
    getMeals({ pageSize: 100 }).then((res) => {
      const result = res.data?.data;
      setMeals(Array.isArray(result) ? result : (result?.data || []));
    });
  }, []);

  const openAssignDrawer = useCallback(() => {
    setInitialValues({ ...INITIAL_FORM, targets: { ...EMPTY_TARGETS } });
    setDrawerOpen(true);
  }, []);

  const hasTargets = (targets) => {
    const tgt = targets || {};
    return tgt.includeAll
      || (tgt.userIds?.length > 0)
      || (tgt.departmentIds?.length > 0)
      || (tgt.locationIds?.length > 0)
      || (tgt.userGroupIds?.length > 0)
      || (tgt.subTenantIds?.length > 0);
  };

  const handleSave = useCallback(async (formData) => {
    if (!hasTargets(formData.targets)) {
      toast.error(t('canteen.selectAtLeastOneTarget', { defaultValue: 'En az bir hedef seçin.' }));
      return false;
    }
    try {
      await assignMealEntitlement({
        mealId: formData.mealId,
        targets: formData.targets,
        // Seçici bayrağı targets nesnesinin İÇİNDE tutuyor; backend DTO'su
        // üst seviyede bekliyor.
        autoApplyOnEnter: !!formData.targets?.autoApplyOnEnter,
        validFrom: formData.validFrom || null,
        validUntil: formData.validUntil || null,
        dailyLimitOverride: formData.dailyLimitOverride === '' || formData.dailyLimitOverride == null
          ? null : Number(formData.dailyLimitOverride),
        monthlyLimitOverride: formData.monthlyLimitOverride === '' || formData.monthlyLimitOverride == null
          ? null : Number(formData.monthlyLimitOverride),
      });
      setRefreshTick((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, [t]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteMealEntitlement(deleteConfirm.id);
      setRefreshTick((p) => p + 1);
    } catch { /* */ }
    setDeleteConfirm({ isOpen: false, id: null, name: '' });
    setDeleting(false);
  }, [deleteConfirm.id]);

  const actionOptions = useMemo(() => {
    if (!canWrite('CN')) return [];
    return [
      {
        type: 'delete',
        label: t('common.delete'),
        onClick: (row) => setDeleteConfirm({ isOpen: true, id: row.id, name: `${row.userFullName} — ${row.mealName}` }),
      },
    ];
  }, [t, canWrite]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'userFullName', label: t('canteen.user'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.userProfilePicture} />
      ), filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'mealName', label: t('canteen.meal'), options: { filter: false, sort: true } },
    { name: 'validFrom', label: t('canteen.validFrom'), options: { filter: false, sort: true, customBodyRender: (v) => v || '-' } },
    { name: 'validUntil', label: t('canteen.validUntil'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'dailyLimitOverride', label: t('canteen.dailyLimitOverride'), options: { filter: false, sort: false, customBodyRender: (v) => v ?? '-' } },
    { name: 'monthlyLimitOverride', label: t('canteen.monthlyLimitOverride'), options: { filter: false, sort: false, customBodyRender: (v) => v ?? '-' } },
    ...(actionOptions.length > 0 ? [{
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => <OptionsMenu actions={actionOptions} row={data[tableMeta.rowIndex]} />,
      },
    }] : []),
  ], [t, data, actionOptions]);

  const schema = useMemo(() => [
    {
      name: 'mealId', label: t('canteen.meal'), type: 'select', grid: 12, required: true,
      options: meals.map((m) => ({ id: m.id, name: m.name })),
      idKey: 'id', labelKey: 'name',
    },
    { name: 'targets', label: '', type: 'assignmentTargets', grid: 12, showAutoApply: true },
    { name: 'validFrom', label: t('canteen.validFrom'), type: 'date', grid: 6 },
    { name: 'validUntil', label: t('canteen.validUntil'), type: 'date', grid: 6 },
    { name: 'dailyLimitOverride', label: t('canteen.dailyLimitOverride'), type: 'number', grid: 6 },
    { name: 'monthlyLimitOverride', label: t('canteen.monthlyLimitOverride'), type: 'number', grid: 6 },
  ], [t, meals]);

  return (
    <Container>
      <DataTable
        title={t('canteen.entitlements')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={data.length}
        pageIndex={0}
        pageSize={data.length || 10}
        canCreateNewRecord={canWrite('CN')}
        onNewButtonClick={openAssignDrawer}
        newButtonLabel={t('canteen.assignEntitlement')}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('common.delete')}
        message={deleteConfirm.name}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={deleting}
      />

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('canteen.assignEntitlement')}
        schema={schema}
        initialValues={initialValues}
        onSave={handleSave}
      />
    </Container>
  );
}
